import { isNoSpeechWarningStartupEnabled } from '@/app-shell/flags/no-speech-startup-warning-flag';
import { logger } from '@/utils/logger/logger';
import type { SessionChunk } from '@heidi/capture/contracts';
import type { ChunkResponse } from '@heidi/session/data';
import { observeNoSpeechMidSessionCompletedChunk } from './noSpeechMidSessionWarning';

export const NO_TRANSCRIPT_CHUNKS_START = 2;
const STARTUP_AUDIO_MS = 90_000;

type WarningPhase = 'waiting' | 'eligible' | 'shown' | 'dismissed' | 'recovered';
type WarningTransport = 'web_sse_v3' | 'web_http_v2' | 'desktop_v1' | 'desktop_v2';
type WarningState = {
  phase: WarningPhase;
  transport: WarningTransport;
  warningInstanceId: string;
  completedIndices: Set<number>;
  completedAudioMs: number;
  firstObservedAtMs: number;
  shownAtMs?: number;
};
const states = new Map<string, WarningState>();
const listeners = new Set<() => void>();
const EMPTY_PHASE: WarningPhase = 'waiting';
const MAX_TRACKED_RECORDINGS = 100;
let nextWarningInstance = 0;

/** Keep the startup detector bounded across long-lived desktop sessions. */
const trimOldRecordings = () => {
  if (states.size <= MAX_TRACKED_RECORDINGS) return;
  const oldestId = states.keys().next().value;
  if (oldestId) states.delete(oldestId);
};

const notify = () => listeners.forEach((listener) => listener());

export const subscribeNoSpeechStartupWarning = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const getNoSpeechStartupWarningPhase = (
  recordingId: string | null
): WarningPhase => (recordingId && states.get(recordingId)?.phase) || EMPTY_PHASE;

type CompletedChunk = {
  sessionId?: string;
  recordingId: string;
  index: number;
  durationMs: number;
  text: string | null | undefined;
  audioEndMs?: number;
  transport: WarningTransport;
};

const logWarningEvent = (
  state: WarningState,
  tag: string,
  eventType: string,
  timing: Record<string, number> = {}
) => {
  logger.info({
    tag,
    event_type: eventType,
    // Opaque per detector instance; never derived from a session, recording, or transcript.
    warning_instance_id: state.warningInstanceId,
    transport: state.transport,
    platform: state.transport.startsWith('desktop') ? 'desktop' : 'web',
    transcription_version: state.transport.endsWith('v3')
      ? 'v3'
      : state.transport.endsWith('v2')
        ? 'v2'
        : 'v1',
    completed_chunk_count: state.completedIndices.size,
    captured_audio_ms: state.completedAudioMs,
    ...timing,
  });
};

/** Each completed chunk index contributes once across SSE revisions or HTTP retries. */
const observeCompletedChunk = ({
  sessionId,
  recordingId,
  index,
  durationMs,
  text,
  audioEndMs,
  transport,
}: CompletedChunk) => {
  observeNoSpeechMidSessionCompletedChunk({
    sessionId,
    recordingId,
    index,
    durationMs,
    text,
    transport,
  });
  try {
    if (!isNoSpeechWarningStartupEnabled()) return;
    let state = states.get(recordingId);
    if (!state) {
      state = {
        phase: 'waiting',
        transport,
        warningInstanceId:
          globalThis.crypto?.randomUUID?.() ??
          `local-${Date.now()}-${++nextWarningInstance}`,
        completedIndices: new Set(),
        completedAudioMs: 0,
        firstObservedAtMs: Date.now(),
      };
      states.set(recordingId, state);
      trimOldRecordings();
    }
    if (state.phase === 'recovered') return;
    if (typeof text === 'string' && text.trim()) {
      if (state.phase === 'shown' || state.phase === 'dismissed') {
        const now = Date.now();
        logWarningEvent(
          state,
          'no_speech_startup_recovered',
          'NO_SPEECH_STARTUP_RECOVERED',
          {
            time_to_recovery_ms: now - state.firstObservedAtMs,
            ...(state.shownAtMs === undefined
              ? {}
              : { shown_to_recovery_ms: now - state.shownAtMs }),
          }
        );
      }
      state.phase = 'recovered';
      notify();
      return;
    }
    if (state.phase !== 'waiting') return;
    if (!state.completedIndices.has(index)) {
      state.completedIndices.add(index);
      state.completedAudioMs += Number.isFinite(durationMs) ? Math.max(0, durationMs) : 0;
    }
    if (
      state.completedIndices.size >= NO_TRANSCRIPT_CHUNKS_START &&
      Math.max(audioEndMs ?? 0, state.completedAudioMs) >= STARTUP_AUDIO_MS
    ) {
      state.phase = 'eligible';
      logWarningEvent(state, 'no_speech_startup_eligible', 'NO_SPEECH_STARTUP_ELIGIBLE', {
        time_to_eligibility_ms: Date.now() - state.firstObservedAtMs,
      });
      notify();
    }
  } catch {
    // Warning observation is passive; malformed chunk data must never fail an upload.
    try {
      logger.warn('Unable to observe startup speech state');
    } catch {
      // Even a telemetry failure must not escape into the upload path.
    }
  }
};

export const observeNoSpeechStartupChunk = (response: ChunkResponse): void => {
  if (response.kind !== 'transcribed' || response.state !== 'persisted') return;
  observeCompletedChunk({
    sessionId: response.session_id,
    recordingId: response.recording_id,
    index: response.chunk_index,
    durationMs: response.duration_ms,
    text: response.text,
    audioEndMs: response.start_offset_ms + response.duration_ms,
    transport: 'web_sse_v3',
  });
};

/** HTTP V2 returns text but no audio offset; completed chunk durations provide the threshold. */
export const observeNoSpeechStartupHttpChunk = (
  chunk: Pick<SessionChunk, 'recordingId' | 'index' | 'duration'> &
    Partial<Pick<SessionChunk, 'sessionId'>>,
  text: string | null | undefined,
  audioEndMs?: number,
  transport: WarningTransport = 'web_http_v2'
): void => {
  observeCompletedChunk({
    sessionId: chunk.sessionId,
    recordingId: chunk.recordingId,
    index: chunk.index,
    durationMs: chunk.duration * 1000,
    text,
    audioEndMs,
    transport,
  });
};

type NativeT11nChunk = {
  index: number;
  text: string;
  audio_info: { duration?: number };
  timestamp_ms: number;
  timestamp_cumulative_ms: number;
};

/** Observe an active native recording from the already-mounted T11n query. */
export const observeNoSpeechStartupT11nRecording = (
  recordingId: string,
  chunks: NativeT11nChunk[],
  sessionId?: string,
  startPosition = 0
): void => {
  const firstChunk = chunks[0];
  const recordingBaseMs = firstChunk
    ? firstChunk.timestamp_cumulative_ms - firstChunk.timestamp_ms
    : 0;
  for (let position = startPosition; position < chunks.length; position++) {
    const chunk = chunks[position]!;
    const next = chunks[position + 1];
    const previous = chunks[position - 1];
    const adjacent =
      next?.index === chunk.index + 1
        ? next.timestamp_cumulative_ms - chunk.timestamp_cumulative_ms
        : previous?.index === chunk.index - 1
          ? chunk.timestamp_cumulative_ms - previous.timestamp_cumulative_ms
          : 0;
    // Native V2 can omit audio_info.duration. Adjacent chunk start times give
    // a conservative captured-audio estimate without using wall-clock delay.
    const durationMs =
      chunk.audio_info.duration ??
      (Number.isFinite(adjacent) ? Math.min(60_000, Math.max(0, adjacent)) : 0);
    observeNoSpeechStartupHttpChunk(
      {
        sessionId,
        recordingId,
        index: chunk.index,
        duration: durationMs / 1000,
      },
      chunk.text,
      chunk.timestamp_cumulative_ms - recordingBaseMs + durationMs,
      'desktop_v2'
    );
  }
};

/** Reset detector state for isolated tests. */
export const resetNoSpeechStartupWarningState = () => {
  states.clear();
  listeners.clear();
};

export const markNoSpeechStartupWarningShown = (recordingId: string): void => {
  const state = states.get(recordingId);
  if (state?.phase !== 'eligible') return;
  state.phase = 'shown';
  state.shownAtMs = Date.now();
  logWarningEvent(state, 'no_speech_startup_shown', 'NO_SPEECH_STARTUP_SHOWN', {
    time_to_show_ms: state.shownAtMs - state.firstObservedAtMs,
  });
  notify();
};

export const dismissNoSpeechStartupWarning = (
  recordingId: string,
  reason: 'close' | 'audio_source_clicked' = 'close'
): void => {
  const state = states.get(recordingId);
  if (state?.phase !== 'shown') return;
  state.phase = 'dismissed';
  if (reason === 'audio_source_clicked') {
    logWarningEvent(
      state,
      'no_speech_startup_audio_source_clicked',
      'NO_SPEECH_STARTUP_AUDIO_SOURCE_CLICKED'
    );
  }
  logWarningEvent(state, 'no_speech_startup_dismissed', 'NO_SPEECH_STARTUP_DISMISSED');
  notify();
};
